<template>
  <eg-modal
    id="create-student-modal"
    :title="student ? $t('common.editStudent') : $t('common.createNewStudent')"
    :closable="true"
    :value="true"
    style="z-index: var(--layout-z-index-modal)"
    @input="$emit('close')"
    @close="$emit('close')"
  >
    <eg-b-form @submit.prevent="essayId ? onSaveAndSelect : onSave">
      <eg-form-group :label="$t('common.firstName')" required>
        <eg-b-form-input
          v-model="firstName"
          :placeholder="$t('common.firstName')"
          :disabled="savingAdd || savingAddAndSelect"
          autofocus
        />
      </eg-form-group>
      <eg-form-group :label="$t('common.lastName')">
        <eg-b-form-input
          v-model="lastName"
          :placeholder="$t('common.lastName')"
          :disabled="savingAdd || savingAddAndSelect"
        />
      </eg-form-group>
      <div v-if="duplicateError" class="create-student-modal__error">
        {{ duplicateError }}
      </div>
      <div class="d-flex flex-gap-1">
        <eg-button
          type="secondary"
          :is-loading="savingAdd"
          :disabled="
            !firstName.trim() ||
            !lastName.trim() ||
            !!duplicateError ||
            savingAddAndSelect
          "
          @click="onSave"
        >
          {{ student ? $t("common.save") : $t("common.addStudent") }}
        </eg-button>
        <eg-button
          v-if="!student && !duplicateStudent"
          type="primary"
          :is-loading="savingAddAndSelect"
          :disabled="!firstName.trim() || !lastName.trim() || savingAdd"
          @click="onSaveAndSelect"
        >
          {{ $t("common.addAndSelectStudent") }}
        </eg-button>
        <eg-button
          v-if="!student && duplicateStudent && essayId"
          type="primary"
          :is-loading="savingSelect"
          :disabled="savingAdd || savingAddAndSelect"
          @click="onSelectExisting"
        >
          {{ $t("common.selectExistingStudent") }}
        </eg-button>
      </div>
    </eg-b-form>
  </eg-modal>
</template>

<script>
import {
  createStudent,
  updateStudent,
  attachStudentToEssay,
} from "@/api/requests/students";
import EgModal from "@/components/global/eg/modals/eg_modal.vue";
import { mapGetters } from "vuex";

export default {
  name: "CreateStudentModal",

  components: { EgModal },

  props: {
    essayId: {
      type: [String, Number],
      default: null,
    },
    student: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      firstName: this.student?.firstName || "",
      lastName: this.student?.lastName || "",
      savingAdd: false,
      savingAddAndSelect: false,
      savingSelect: false,
    };
  },

  computed: {
    ...mapGetters("students", ["students"]),

    duplicateStudent() {
      const first = this.firstName.trim().toLowerCase();
      const last = this.lastName.trim().toLowerCase();
      if (!first || !last) return null;
      return (
        this.students.find(
          (s) =>
            s.id !== this.student?.id &&
            (s.firstName || "").trim().toLowerCase() === first &&
            (s.lastName || "").trim().toLowerCase() === last
        ) || null
      );
    },

    duplicateError() {
      if (!this.duplicateStudent) return null;
      return this.$t("common.studentAlreadyExists", {
        name: `${this.firstName.trim()} ${this.lastName.trim()}`,
      });
    },
  },

  methods: {
    async onSave() {
      await this.submit({ andSelect: false });
    },

    async onSaveAndSelect() {
      await this.submit({ andSelect: true });
    },

    async onSelectExisting() {
      if (!this.duplicateStudent || !this.essayId) return;
      this.savingSelect = true;
      try {
        const { data: essay } = await attachStudentToEssay({
          essayId: this.essayId,
          studentId: this.duplicateStudent.id,
        });
        this.$emit("studentCreatedAndSelected", essay);
      } catch (e) {
        this.$showToastError(e.response?.data?.error);
      } finally {
        this.savingSelect = false;
      }
    },

    async submit({ andSelect }) {
      if (
        !this.firstName.trim() ||
        !this.lastName.trim() ||
        this.duplicateStudent
      )
        return;

      if (andSelect) {
        this.savingAddAndSelect = true;
      } else {
        this.savingAdd = true;
      }

      try {
        if (this.student) {
          const { data } = await updateStudent({
            studentId: this.student.id,
            firstName: this.firstName.trim(),
            lastName: this.lastName.trim(),
          });
          this.$store.commit("students/updateStudent", data);
          this.$emit("studentSaved", data);
        } else {
          const { data: student } = await createStudent({
            firstName: this.firstName.trim(),
            lastName: this.lastName.trim(),
          });
          this.$store.commit("students/addStudent", student);

          if (andSelect) {
            if (this.essayId) {
              const { data: essay } = await attachStudentToEssay({
                essayId: this.essayId,
                studentId: student.id,
              });
              this.$emit("studentCreatedAndSelected", essay);
            } else {
              this.$emit("studentCreated", student);
            }
          } else {
            this.$emit("studentCreated", student);
          }
        }
        this.reset();
        if (andSelect || this.student) {
          this.$emit("close");
        }
      } catch (e) {
        this.$showToastError(e.response?.data?.error);
      } finally {
        this.savingAdd = false;
        this.savingAddAndSelect = false;
      }
    },

    reset() {
      this.firstName = "";
      this.lastName = "";
    },
  },
};
</script>

<style scoped lang="scss">
.create-student-modal__error {
  color: var(--color-text-semantic-onError);
  font-size: var(--typography-font-size-fs-300);
  margin-bottom: 0.5rem;
}
</style>
